import type { FC, ReactNode } from 'react';
import React, { Fragment, useCallback, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import MenuGroup from '@atlaskit/menu/menu-group';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { useFolderChildren } from '@confluence/content-tree-search/entry-points/useFolderChildren.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import {
	VIEW_PAGE,
	WHITEBOARD_BOARD,
	DATABASE_VIEW,
	SLIDES_VIEW,
	EMBED_VIEW,
	FOLDER_VIEW,
} from '@confluence/named-routes/entry-points/routes.ts';
import { useIsSuperAdmin } from '@confluence/super-admin/entry-points/AdminKeyQuery.ts';
import type { HoverPageCardProps } from '@confluence/page-card/entry-points/useHoverPageCard.ts';

import { SpaceViewsListItem } from './SpaceViewsListItem.tsx';

export type SpaceViewsPage = {
	title?: string | null;
	id?: string | null;
	hasRestrictions?: boolean;
	hasInheritedRestrictions?: boolean;
	displayEmoji?: string;
	type?: string;
	subType?: string;
	hasChildren?: boolean;
};

type SpaceViewsListProps = {
	pagesList: ReadonlyArray<SpaceViewsPage>;
	onItemClick?: (id: string, index?: number) => void;
	spaceKey?: string;
	contentId?: string;
	shouldRenderAfterIcon?: boolean;
	renderTitle?: ({ title }: { title: string }) => ReactNode;
	enableFolderExpansion?: boolean;
	isNested?: boolean;
} & Partial<HoverPageCardProps>;

const styles = cssMap({
	resultState: {
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- Matches the page-tree empty state row.
		minHeight: '36px',
		display: 'flex',
		alignItems: 'center',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
});

const FOLDER_LOADING_SPINNER_SIZE = 16;

type ExpandableFolderSearchResultProps = Omit<SpaceViewsListProps, 'pagesList' | 'isNested'> & {
	page: SpaceViewsPage;
	index: number;
	renderItem: (
		page: SpaceViewsPage,
		index: number,
		nestedContent?: ReactNode,
		isExpanded?: boolean,
		onExpansionToggle?: (isExpanded: boolean) => void,
	) => ReactNode;
};

const ExpandableFolderSearchResult: FC<ExpandableFolderSearchResultProps> = ({
	page,
	index,
	renderItem,
	...listProps
}) => {
	const intl = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);
	const folderId = page.id?.split('/').pop() ?? '';
	const { folderChildren, loading, error, isLoadingMore, loadMore } = useFolderChildren(
		folderId,
		isExpanded,
	);
	const handleLoadMore = useCallback(() => {
		void loadMore?.();
	}, [loadMore]);
	const nestedContent = (
		<>
			{folderChildren.length > 0 ? (
				<SpaceViewsList
					{...listProps}
					pagesList={folderChildren.map((child) => ({
						...child,
						type: child.type ?? undefined,
						subType: child.subType ?? undefined,
					}))}
					enableFolderExpansion
					isNested
				/>
			) : null}
			{isExpanded && loading ? (
				<Box as="li" xcss={styles.resultState}>
					<Spinner
						size={FOLDER_LOADING_SPINNER_SIZE}
						label={intl.formatMessage(i18n.loadingFolder, { title: page.title })}
						testId="folder-children-loading"
					/>
				</Box>
			) : null}
			{error ? (
				<Box as="li" xcss={styles.resultState}>
					<ErrorDisplay error={error}>
						<Box role="alert">
							<Text color="color.text.subtlest">
								<FormattedMessage {...i18n.folderLoadError} />
							</Text>
						</Box>
					</ErrorDisplay>
				</Box>
			) : null}
			{isExpanded && !loading && !error && folderChildren.length === 0 ? (
				<Box as="li" xcss={styles.resultState}>
					<Text color="color.text.subtlest">
						<FormattedMessage {...i18n.emptyFolder} />
					</Text>
				</Box>
			) : null}
			{loadMore ? (
				<Box as="li">
					<Button
						appearance="subtle"
						spacing="compact"
						onClick={handleLoadMore}
						isLoading={isLoadingMore}
					>
						<FormattedMessage {...i18n.loadMore} />
					</Button>
				</Box>
			) : null}
		</>
	);

	return <>{renderItem(page, index, nestedContent, isExpanded, setIsExpanded)}</>;
};

const getUrlByType = (type) => {
	const typeToUrl = {
		page: VIEW_PAGE,
		whiteboard: WHITEBOARD_BOARD,
		database: DATABASE_VIEW,
		slide: SLIDES_VIEW,
		embed: EMBED_VIEW,
		folder: FOLDER_VIEW,
	};

	return type && typeToUrl[type] ? typeToUrl[type] : VIEW_PAGE;
};

export const SpaceViewsList: FC<SpaceViewsListProps> = ({
	pagesList,
	onItemClick,
	spaceKey,
	contentId,
	closeAllHoverPageCards = () => {},
	onEnterHoverTarget = () => {},
	onLeaveHoverTarget = () => {},
	onBlurHoverTarget = () => {},
	shouldRenderAfterIcon,
	renderTitle = ({ title }) => title,
	enableFolderExpansion = false,
	isNested = false,
}) => {
	const intl = useIntl();
	const { isSuperAdmin } = useIsSuperAdmin();
	const [openQuickActionsId, setOpenQuickActionsId] = useState<string | null>(null);
	const [focusedQuickActionsId, setFocusedQuickActionsId] = useState<string | null>(null);
	const renderItem = (
		page: SpaceViewsPage,
		index: number,
		nestedContent?: ReactNode,
		isExpanded?: boolean,
		onExpansionToggle?: (isExpanded: boolean) => void,
	): ReactNode => {
		const { title, id, hasRestrictions, hasInheritedRestrictions, displayEmoji, type, subType } =
			page;
		const isCurrentPage = Boolean(contentId && contentId === id);
		let parsedId = id;
		// databases, slides, embeds, and folders currently have their id returned as 'activation/ACTIVATION_ID/ENTITY_ID', so we are parsing the entity id
		if ((type === 'database' || type === 'embed' || type === 'slide' || type === 'folder') && id) {
			parsedId = id.split('/').pop() || null;
		}
		const contentLink =
			parsedId && spaceKey
				? getUrlByType(type).toUrl({
						spaceKey,
						contentId: parsedId,
					})
				: '';

		const item = {
			id: parsedId || '',
			hasRestrictions,
			hasInheritedRestrictions,
			data: {
				type,
				subType,
			},
		};
		return (
			<SpaceViewsListItem
				intl={intl}
				url={contentLink}
				title={title || ' '}
				displayEmoji={displayEmoji}
				contentType={type}
				onItemClick={onItemClick}
				key={id}
				index={index}
				isSelected={isCurrentPage}
				spaceKey={spaceKey || ''}
				item={item}
				closeAllHoverPageCards={closeAllHoverPageCards}
				isSuperAdmin={isSuperAdmin}
				isQuickActionsOpen={openQuickActionsId === item.id}
				setOpenQuickActionsId={setOpenQuickActionsId}
				isQuickActionsFocused={focusedQuickActionsId === item.id}
				setFocusedQuickActionsId={setFocusedQuickActionsId}
				setIsEditingTitleId={noop}
				onEnterHoverTarget={onEnterHoverTarget}
				onLeaveHoverTarget={onLeaveHoverTarget}
				onBlurHoverTarget={onBlurHoverTarget as (e) => void}
				shouldRenderAfterIcon={shouldRenderAfterIcon}
				renderTitle={renderTitle}
				isExpanded={isExpanded}
				onExpansionToggle={onExpansionToggle}
				nestedContent={nestedContent}
			/>
		);
	};

	const renderedItems = pagesList.map((page, index) => {
		if (
			enableFolderExpansion &&
			page.type === 'folder' &&
			Boolean(page.id) &&
			page.hasChildren !== false
		) {
			return (
				<ExpandableFolderSearchResult
					key={page.id}
					page={page}
					index={index}
					renderItem={renderItem}
					onItemClick={onItemClick}
					spaceKey={spaceKey}
					contentId={contentId}
					closeAllHoverPageCards={closeAllHoverPageCards}
					onEnterHoverTarget={onEnterHoverTarget}
					onLeaveHoverTarget={onLeaveHoverTarget}
					onBlurHoverTarget={onBlurHoverTarget}
					shouldRenderAfterIcon={shouldRenderAfterIcon}
					renderTitle={renderTitle}
				/>
			);
		}

		return renderItem(page, index);
	});

	return isNested ? (
		<Fragment>{renderedItems}</Fragment>
	) : (
		<Fragment>
			<MenuGroup role="list">{renderedItems}</MenuGroup>
		</Fragment>
	);
};

const noop = () => Promise.resolve();

const i18n = defineMessages({
	loadingFolder: {
		id: 'space-views.content-tree-search.folder.loading',
		defaultMessage: 'Loading content in {title}',
		description: 'Message shown while loading the direct children of a folder search result.',
	},
	folderLoadError: {
		id: 'space-views.content-tree-search.folder.load-error',
		defaultMessage: 'We couldn’t load this folder’s content.',
		description: 'Error shown when the direct children of a folder search result cannot be loaded.',
	},
	emptyFolder: {
		id: 'space-views.content-tree-search.folder.empty',
		defaultMessage: 'There’s nothing in this folder yet',
		description: 'Message shown when an expanded folder search result has no direct children.',
	},
	loadMore: {
		id: 'space-views.content-tree-search.folder.load-more',
		defaultMessage: 'Show more',
		description: 'Button label for loading more children of an expanded folder search result.',
	},
});
